import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.object.assign.js';
import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import React, { useRef } from 'react';
import { DatePicker as DatePicker$1 } from '@mui/x-date-pickers/DatePicker';
import classNames from 'classnames';
import { getStatusIcon, CalendarIcon, ChevronUpIcon, ChevronDownIcon, ChevronRightIcon, ChevronLeftIcon } from '@cognite/cogs-icons';
import { IconWrapper } from '@cognite/cogs-utils';
import { Body } from '../../tokens/typography/typography.js';
import { COGS_DEFAULT_DATE_FORMAT } from '../../util/constants.js';
import 'core-js/modules/es.error.to-string.js';
import 'core-js/modules/es.array.includes.js';
import 'core-js/modules/es.date.to-string.js';
import 'core-js/modules/es.object.to-string.js';
import 'core-js/modules/es.regexp.exec.js';
import 'core-js/modules/es.regexp.to-string.js';
import 'core-js/modules/es.string.includes.js';
import 'core-js/modules/es.string.replace.js';
import 'dayjs';
import '../../tokens/color-tokens/index.js';
import '../../tokens/elevation-tokens/index.js';
import 'core-js/modules/es.array.filter.js';
import 'core-js/modules/es.array.join.js';
import { TextLabel } from '../_technical/text-label/text-label.js';
import 'core-js/modules/es.array.map.js';
import '@mui/base/Button';
import 'core-js/modules/es.array.push.js';
import '@mui/material';
import '@mui/material/Fade';
import { useConfigContextLoose } from '../config-provider/config-provider.js';
import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import { Button } from '../button/button.js';
import { LocalizationProvider } from '../localization-provider/localization-provider.js';
import { DATEPICKER_POPPER_BASE_MODIFIERS } from './constants.js';

/**
 * DatePicker
 * =============================================================================
 * Describe what your component does, and why it could be useful for users.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-datepicker--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <DatePicker>
 *   My datepicker
 * </DatePicker>
 *
 * <DatePicker subject="bird">
 *   My advanced datepicker
 * </DatePicker>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param subject - Explain here what your prop does
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-datepicker--base)
 */
var DatePicker = /*#__PURE__*/React.forwardRef(function (_a, outerRef) {
  var _b;
  var disabled = _a.disabled,
    disablePopperPortal = _a.disablePopperPortal,
    format = _a.format,
    fullWidth = _a.fullWidth,
    helpText = _a.helpText,
    id = _a.id,
    label = _a.label,
    localizationProviderProps = _a.localizationProviderProps,
    _a$size = _a.size,
    size = _a$size === undefined ? 'medium' : _a$size,
    status = _a.status,
    statusText = _a.statusText,
    _a$variant = _a.variant,
    variant = _a$variant === undefined ? 'outline' : _a$variant,
    className = _a.className,
    dataTestId = _a['data-testid'],
    _a$displayWeekNumber = _a.displayWeekNumber,
    displayWeekNumber = _a$displayWeekNumber === undefined ? false : _a$displayWeekNumber,
    shortcuts = _a.shortcuts,
    popperContainer = _a.popperContainer,
    popperAnchor = _a.popperAnchor,
    otherProps = __rest(_a, ["disabled", "disablePopperPortal", "format", "fullWidth", "helpText", "id", "label", "localizationProviderProps", "size", "status", "statusText", "variant", "className", 'data-testid', "displayWeekNumber", "shortcuts", "popperContainer", "popperAnchor"]);
  var innerRef = useRef(null);
  var ref = outerRef || innerRef;
  var _useConfigContextLoos = useConfigContextLoose(),
    dateFormat = _useConfigContextLoos.dateFormat;
  var formatLocal = (_b = format !== null && format !== undefined ? format : dateFormat) !== null && _b !== undefined ? _b : COGS_DEFAULT_DATE_FORMAT;
  var shouldDisplayLabel = !!label && variant !== 'ghost';
  var Icon = getStatusIcon(status);
  var customPopperContainerSetting = popperContainer ? {
    container: popperContainer
  } : {};
  var customPopperAnchorSetting = popperAnchor ? {
    anchorEl: popperAnchor
  } : {};
  return jsx(LocalizationProvider, {
    localizationProviderProps: localizationProviderProps,
    children: jsxs("div", {
      className: classNames('cogs-v10', 'cogs-datepicker-container', 'cogs-input-container', fullWidth && 'cogs-datepicker-container--full-width'),
      "data-testid": dataTestId,
      children: [shouldDisplayLabel && (typeof label === 'string' ? jsx(TextLabel, {
        htmlFor: id,
        text: label
      }) : jsx(TextLabel, Object.assign({
        htmlFor: id
      }, label))), jsx(DatePicker$1, Object.assign({}, otherProps, {
        className: classNames('cogs-v10', className),
        disabled: disabled,
        format: formatLocal,
        ref: ref,
        displayWeekNumber: displayWeekNumber,
        slots: {
          openPickerButton: function openPickerButton(props) {
            var _a;
            return jsx(Button, {
              icon: jsx(CalendarIcon, {}),
              type: "ghost",
              size: size === 'small' ? 'small' : 'medium',
              onClick: props.onClick,
              disabled: props.disabled,
              "aria-label": (_a = props['aria-label']) !== null && _a !== undefined ? _a : 'Open date picker',
              "data-testid": "open-date-picker-button"
            });
          },
          switchViewButton: function switchViewButton(props) {
            var _a;
            return jsx(Button, {
              "aria-label": "Switch date picker view",
              icon: ((_a = props === null || props === undefined ? undefined : props.ownerState) === null || _a === undefined ? undefined : _a.view) !== 'day' ? jsx(ChevronUpIcon, {}) : jsx(ChevronDownIcon, {}),
              size: "small",
              type: "ghost"
            });
          },
          nextIconButton: function nextIconButton(props) {
            return jsx(Button, {
              "aria-label": "Go to next month",
              disabled: props === null || props === undefined ? undefined : props.disabled,
              onClick: props === null || props === undefined ? undefined : props.onClick,
              icon: jsx(ChevronRightIcon, {}),
              size: "small",
              type: "ghost"
            });
          },
          previousIconButton: function previousIconButton(props) {
            return jsx(Button, {
              "aria-label": "Go to previous month",
              disabled: props === null || props === undefined ? undefined : props.disabled,
              onClick: props === null || props === undefined ? undefined : props.onClick,
              icon: jsx(ChevronLeftIcon, {}),
              size: "small",
              type: "ghost"
            });
          }
        },
        slotProps: {
          day: {
            className: 'cogs-v10 cogs-datepicker-day',
            disableRipple: true
          },
          desktopPaper: {
            className: 'cogs-v10 cogs-datepicker-desktoppaper'
          },
          popper: Object.assign(Object.assign({
            className: 'cogs-v10 cogs-datepicker-popper',
            disablePortal: disablePopperPortal,
            modifiers: [].concat(DATEPICKER_POPPER_BASE_MODIFIERS)
          }, customPopperContainerSetting), customPopperAnchorSetting),
          textField: {
            className: classNames('cogs-v10', 'cogs-datepicker', fullWidth && 'cogs-datepicker-full-width'),
            hiddenLabel: true,
            id: id,
            InputProps: {
              className: classNames('cogs-v10', 'cogs-input', "cogs-input--size-" + size, "cogs-input--variant-" + variant, "cogs-input--status-" + status, 'cogs-input-with-no-right-padding', disabled && "cogs-input-disabled", fullWidth && "cogs-input-full-width")
            }
          },
          shortcuts: {
            className: 'cogs-v10 cogs-datepicker-shortcuts',
            items: shortcuts,
            changeImportance: 'set'
          }
        }
      })), helpText && jsx(Body, {
        size: "x-small",
        className: "cogs-v10 cogs-input-help-text",
        children: helpText
      }), status && statusText && jsxs("div", {
        className: "cogs-v10 cogs-input-status-text-container",
        children: [Icon && jsx(IconWrapper, {
          size: 12,
          children: jsx(Icon, {
            className: classNames('cogs-v10', 'cogs-input-status-text', "cogs-input-status-text--status-" + status)
          })
        }), jsx(Body, {
          size: "x-small",
          className: classNames('cogs-v10', 'cogs-input-status-text', "cogs-input-status-text--status-" + status),
          children: statusText
        })]
      })]
    })
  });
});

export { DatePicker };
